// PURE orchestration brain for `ss dev up` — no I/O, fully unit-testable. The
// `ss dev up` subcommand is a thin executor that calls `decideNextAction` in a loop
// and runs the chosen step (reusing the existing `ss dev` subcommands), so the whole
// /dev-start workflow is DETERMINISTIC TypeScript instead of Haiku-interpreted prose.

export interface UpFlags {
  restart?: boolean;
  clean?: boolean;
  reset?: boolean;
  backendOnly?: boolean;
  frontendOnly?: boolean;
}

export interface OrchestratorState {
  cacheExists: boolean;
  /** cache missing / >7d / a tracked source file changed since last detect. */
  cacheStale: boolean;
  /** cache predates self-repair (no config_status field) → force a fresh detect. */
  hasConfigStatus: boolean;
  /** OUR backend child is the live listener (strict identity — see dev-occupancy). */
  backendRunning: boolean;
  frontendRunning: boolean;
  configStatus?: 'ok' | 'mismatch' | 'occupied';
  /**
   * appsettings.Local.json is absent → auto-seed it before launch (every branch, no
   * prompt) so dev always runs in Local mode. `undefined` = cache predates this field
   * → force a fresh detect (handled in decideNextAction).
   */
  localConfigMissing?: boolean;
  legacyHttpsProfile?: boolean;
  frontendDepsReady?: boolean;
}

export type Action =
  | { kind: 'idempotent_summary' }
  | { kind: 'detect' }
  | { kind: 'normalize_https' }
  | { kind: 'seed_local_config' }
  | { kind: 'needs_input'; reason: 'mismatch' | 'occupied' }
  | { kind: 'ensure_deps' }
  | { kind: 'restart_cycle'; build: 'incremental' | 'clean' | 'none' }
  | { kind: 'launch' };

/**
 * Decide the NEXT step given the current state + flags. Pure + total. The executor
 * runs the step, re-derives state, and calls again — until a terminal step
 * (idempotent_summary / needs_input / launch / restart_cycle). `detect`,
 * `normalize_https` and `ensure_deps` loop back. The caller MUST clear `reset` after
 * the first detect so it does not re-trigger detect forever.
 */
export function decideNextAction(state: OrchestratorState, flags: UpFlags): Action {
  // A normal (non-restart) rerun where our own servers are already up + cache fresh:
  // reuse them, just render the cartouche. Restart never short-circuits.
  if (!flags.restart && state.backendRunning && state.frontendRunning && !state.cacheStale) {
    return { kind: 'idempotent_summary' };
  }

  // Resolve config first: detect when missing / stale / pre-self-repair / pre-seed-field
  // (localConfigMissing absent → legacy cache) / --reset.
  if (
    !state.cacheExists ||
    state.cacheStale ||
    !state.hasConfigStatus ||
    state.localConfigMissing === undefined ||
    flags.reset === true
  ) {
    return { kind: 'detect' };
  }

  // Transparently rewrite a legacy dual-https Local profile to HTTP-only (no prompt).
  if (state.legacyHttpsProfile === true) {
    return { kind: 'normalize_https' };
  }

  // Always materialize appsettings.Local.json first (silent, every branch) so dev runs
  // in Local mode. BEFORE needs_input: we create the file, then any PORT problem still
  // prompts on the next detect pass.
  if (state.localConfigMissing === true) {
    return { kind: 'seed_local_config' };
  }

  // A real PORT problem needs a human decision (api≠vite mismatch, or ports occupied by
  // another branch). The Local file's existence is no longer a prompt (auto-seeded above).
  if (state.configStatus && state.configStatus !== 'ok') {
    return { kind: 'needs_input', reason: state.configStatus };
  }

  // config_status === 'ok' from here. W2: NO "Proceed with these ports?" gate.
  if (flags.restart === true) {
    const build: 'incremental' | 'clean' | 'none' = flags.frontendOnly ? 'none' : flags.clean ? 'clean' : 'incremental';
    return { kind: 'restart_cycle', build };
  }

  // Install frontend deps on a fresh checkout before the normal launch.
  if (state.frontendDepsReady === false && !flags.backendOnly) {
    return { kind: 'ensure_deps' };
  }

  return { kind: 'launch' };
}

/** Backoff schedule for the readiness poll (P3): grow ×1.5, capped. Pure. */
export function nextWaitInterval(prev: number, cap: number): number {
  return Math.min(cap, Math.round(prev * 1.5));
}

export interface BackfillEvidence {
  /** The readiness probe got an HTTP serving status on OUR /api/config/features this pass. */
  backendReady: boolean;
  /** WE spawned the frontend this pass (it is in `launched`, not `skipped`). */
  frontendLaunched: boolean;
  /** Current sidecar listenPid for the side: a number (captured), null (gap), or undefined (no record). */
  backendListenPid: number | null | undefined;
  frontendListenPid: number | null | undefined;
}

/**
 * Decide which sides may have their sidecar `listenPid` BACKFILLED from the live listener
 * after an `ss dev up` pass. Pure — the executor does the I/O (read sidecar + live PIDs).
 *
 * WHY THIS EXISTS: `ss dev start`'s bounded post-launch poll can expire before a slow,
 * first-of-the-branch `dotnet run` (build + bind) ever binds the port, so the sidecar keeps
 * `listenPid=null`. The strict ownership check (dev-occupancy, used by status/summary) then
 * DISOWNS our own healthy backend — the cartouche paints `✗ DOWN`, the command exits 1, and
 * a caller reads that as "broken" and loops into needless restarts (or, worse, kills the
 * healthy server). Once the readiness probe has served an HTTP 200 on OUR endpoint, the live
 * listener IS ours — recording it closes the race for this render AND every later status call.
 *
 * It is NOT a blind port adopt (the rule the strict path exists to enforce): a side is
 * eligible ONLY with positive ownership proof gathered THIS pass — backend ⇐ the readiness
 * probe served (a foreign squatter would not answer /api/config/features), frontend ⇐ we
 * spawned it — AND only when there is a real gap to fill (`listenPid === null`). A side with
 * no record (undefined) is never fabricated, and an already-captured pid is never overwritten
 * (so a stale-but-present pid still classifies a dead server DOWN, preserving the guarantee).
 */
export function listenPidBackfillTargets(e: BackfillEvidence): { backend: boolean; frontend: boolean } {
  return {
    backend: e.backendReady && e.backendListenPid === null,
    frontend: e.frontendLaunched && e.frontendListenPid === null,
  };
}

/** Reconstruct the user-facing flag list (for the needs_input re-entry hint + spawns). */
export function upFlagsToArgs(flags: UpFlags): string[] {
  const out: string[] = [];
  if (flags.restart) out.push('--restart');
  if (flags.clean) out.push('--clean');
  if (flags.reset) out.push('--reset');
  if (flags.backendOnly) out.push('--backend-only');
  if (flags.frontendOnly) out.push('--frontend-only');
  return out;
}

export interface NeedsInputInput {
  reason: 'mismatch' | 'occupied';
  gitBranch: string | null;
  apiPort: number;
  webPort: number;
  proposedPorts: { api: number; web: number } | null;
  mismatch: { api_port: number; vite_port: number; launch_profile: string; vite_source: string | null } | null;
  apiBusyPid: number | null;
  webBusyPid: number | null;
  flags: UpFlags;
}

export interface NeedsInputEnvelope {
  status: 'needs_input';
  reason: 'mismatch' | 'occupied';
  message: string;
  git_branch: string | null;
  current_ports: { api: number; web: number };
  proposed_ports: { api: number; web: number } | null;
  mismatch: NeedsInputInput['mismatch'];
  reentry: { configure_ports_only: string; then: string };
}

/**
 * Build the needs_input envelope the SKILL turns into ONE AskUserQuestion. Pure.
 * needs_input is now PORTS-ONLY: appsettings.Local.json is auto-seeded (no SQL prompt),
 * so the only human decisions left are an api≠vite mismatch or ports occupied by another
 * branch — both resolved by choosing ports, then re-running `ss dev up`.
 */
export function buildNeedsInputEnvelope(input: NeedsInputInput): NeedsInputEnvelope {
  const message =
    input.reason === 'mismatch'
      ? `Backend launch port ${input.mismatch?.api_port ?? input.apiPort} ≠ frontend VITE_API_URL :${input.mismatch?.vite_port ?? '?'} (${input.mismatch?.vite_source ?? 'env'}).`
      : `Default ports busy on branch ${input.gitBranch ?? '?'} (api pid ${input.apiBusyPid ?? '?'} / web pid ${input.webBusyPid ?? '?'}).`;

  const then = ['ss dev up', ...upFlagsToArgs(input.flags), '--json'].join(' ');
  return {
    status: 'needs_input',
    reason: input.reason,
    message,
    git_branch: input.gitBranch,
    current_ports: { api: input.apiPort, web: input.webPort },
    proposed_ports: input.proposedPorts,
    mismatch: input.mismatch,
    reentry: {
      configure_ports_only: `ss dev configure --json --spec '{"apiPort":<api>,"webPort":<web>}'`,
      then,
    },
  };
}

// ── First-seed of appsettings.Local.json (confirm/edit) ─────────────────────────

export interface SeedConfigInput {
  gitBranch: string | null;
  apiPort: number;
  webPort: number;
  /** SQL host the connection string will target (default from the base config). */
  server: string;
  /** Per-branch database (created on first run if missing). */
  database: string;
  /** Proposed app admin login (default DEFAULT_ADMIN_EMAIL or the project's existing one). */
  adminEmail: string;
  /** Freshly generated strong password — shown to the user, editable before write. */
  adminPassword: string;
  flags: UpFlags;
}

export interface SeedConfigEnvelope {
  status: 'needs_input';
  reason: 'seed_config';
  message: string;
  git_branch: string | null;
  proposal: {
    api_port: number;
    web_port: number;
    server: string;
    database: string;
    admin_email: string;
    admin_password: string;
    auth_mode: 'windows';
  };
  /**
   * Ready-to-run command for the ACCEPT-DEFAULTS path (the SKILL fills it from the
   * possibly-edited proposal otherwise). Writes appsettings.Local.json with the
   * connection string + JWT + mandatory Security.InitialAdmin, then ensures the DB
   * exists. `then` relaunches the deterministic flow.
   */
  reentry: { configure: string; then: string };
}

/**
 * Build the envelope the SKILL turns into ONE confirm/edit prompt on the FIRST seed
 * of appsettings.Local.json. Pure. Unlike the silent legacy seed, the user confirms
 * the SQL server + per-branch database + admin login, and a generated strong password,
 * before any secret is written (the dev-start contract's "ask me to confirm/edit").
 */
export function buildSeedConfigEnvelope(input: SeedConfigInput): SeedConfigEnvelope {
  const then = ['ss dev up', ...upFlagsToArgs(input.flags), '--json'].join(' ');
  const spec = {
    apiPort: input.apiPort,
    webPort: input.webPort,
    authMode: 'windows',
    sqlServer: input.server,
    sqlDatabase: input.database,
    initialAdminEmail: input.adminEmail,
    initialAdminPassword: input.adminPassword,
    ensureDatabase: true,
  };
  return {
    status: 'needs_input',
    reason: 'seed_config',
    message:
      `First launch on branch ${input.gitBranch ?? '?'} — creating appsettings.Local.json. ` +
      `Confirm or edit the SQL server, database and admin login below; the database is created if it does not exist.`,
    git_branch: input.gitBranch,
    proposal: {
      api_port: input.apiPort,
      web_port: input.webPort,
      server: input.server,
      database: input.database,
      admin_email: input.adminEmail,
      admin_password: input.adminPassword,
      auth_mode: 'windows',
    },
    reentry: {
      configure: `ss dev configure --json --spec '${JSON.stringify(spec)}'`,
      then,
    },
  };
}
